import React, { useState, useEffect, useRef, useCallback } from 'react'
import { joinClasses } from 'utils/joinClasses'
import { useDispatch } from 'react-redux'
import { Typography } from '@frontend/design_system'
import { getBase64 } from './utils'
import { CustomFile, UploadFileProps } from './types'
import { useDidUpdate } from 'hooks/useDidUpdate'
import { MAX_FILES_SIZE } from 'constants/files'
import { setNotificationMessage } from 'redux/notifications/slice'
import { UPLOAD_FILE_FORMAT_ERROR, UPLOAD_FILE_SIZE_ERROR } from 'texts/uiTexts'
import styles from './styles.module.scss'
import { DocumentItem } from '../DocumentItem'

export const UploadFile = ({
  formats,
  onUpload,
  multiple,
  initFiles = [],
  label,
  error,
  disabled,
  renderConfirmationModal,
  variant = 'default',
  columns = 4,
  gapVariant = 'primary',
  onError,
  limitSizeByFile = true,
}: UploadFileProps) => {
  const [dragging, setDragging] = useState(false)
  const [files, setFiles] = useState(initFiles)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  const drag = useRef<HTMLDivElement>(null)
  const drop = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  const dispatch = useDispatch()

  useDidUpdate(() => {
    if (files) {
      onUpload(files)
    }
  }, [files])

  const handleUpload = async (newFiles: CustomFile[] | FileList) => {
    if (!multiple && files?.length) {
      return
    }

    let validFiles = Array.from(newFiles)
    if (formats) {
      validFiles = validFiles.filter((file) =>
        formats.some((format) => file.name.toLowerCase().endsWith(format))
      )
    }

    if (newFiles.length !== validFiles.length) {
      onError?.(UPLOAD_FILE_FORMAT_ERROR)
    }

    const allFiles = [...files, ...validFiles]
    const fullSize = allFiles.reduce((size, file) => size + file.size, 0)

    if (limitSizeByFile) {
      const oversizedFile = validFiles.find(
        (file) => file.size > MAX_FILES_SIZE
      )
      if (oversizedFile) {
        dispatch(
          setNotificationMessage({
            notificationMessage: UPLOAD_FILE_SIZE_ERROR,
            type: 'error',
          })
        )
        return
      }
    } else {
      if (fullSize > MAX_FILES_SIZE) {
        dispatch(
          setNotificationMessage({
            notificationMessage: UPLOAD_FILE_SIZE_ERROR,
            type: 'error',
          })
        )
        return
      }
    }

    if (validFiles?.length) {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i] as CustomFile
        if (validFiles[i].type.includes('image')) {
          file.url = await getBase64(file)
        }

        file.id = `${validFiles[i].name}-${new Date()}`
      }

      setFiles((prevFiles) => [...prevFiles, ...validFiles])
    }
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      setDragging(false)

      handleUpload(e.dataTransfer ? [...e.dataTransfer.files] : [])
    },
    [files]
  )

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.target !== drag.current) {
      setDragging(true)
    }
    if (input.current) {
      input.current.value = ''
    }
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.target === drag.current) {
      setDragging(false)
    }
  }

  const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleUpload(e.target.files)
    }
    if (input.current) {
      input.current.value = ''
    }
  }

  const onDelete = (fileId?: string) => {
    setFiles((prevFiles) => prevFiles.filter(({ id }) => fileId !== id))
  }

  const openFileDialog = () => {
    if (input.current && !disabled) {
      input.current.click()
    }
  }

  useEffect(() => {
    if (drop.current) {
      drop.current.addEventListener('dragover', handleDragOver)
      drop.current.addEventListener('drop', handleDrop)
      drop.current.addEventListener('dragenter', handleDragEnter)
      drop.current.addEventListener('dragleave', handleDragLeave)

      return () => {
        if (drop.current) {
          drop.current.removeEventListener('dragover', handleDragOver)
          drop.current.removeEventListener('drop', handleDrop)
          drop.current.removeEventListener('dragenter', handleDragEnter)
          drop.current.removeEventListener('dragleave', handleDragLeave)
        }
      }
    }
  }, [handleDrop])

  const handleModalConfirm = () => {
    setFiles([])
    setIsConfirmationModalOpen(false)
  }

  const handleModalClose = () => {
    setIsConfirmationModalOpen(false)
  }

  return (
    <div
      className={joinClasses(
        styles['upload-wrapper'],
        styles[variant],
        styles[`columns-${columns}`],
        styles[`gap-${gapVariant}`]
      )}
    >
      {isConfirmationModalOpen &&
        renderConfirmationModal?.(handleModalClose, handleModalConfirm)}
      {!!files.length && (
        <div className={styles['files-wrapper']}>
          {files.map(({ url, type, id, name }) => (
            <DocumentItem
              type={type}
              url={url || ''}
              id={id}
              key={id}
              name={name}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {(!files?.length || multiple) && (
        <div
          ref={drop}
          onClick={openFileDialog}
          className={joinClasses(
            styles['upload-box'],
            styles[variant],
            [styles.dragging, dragging],
            [styles.error, !!error],
            [styles.disabled, disabled]
          )}
          data-testid="upload-box"
        >
          <div className={styles['upload-box-content']}>
            <input
              ref={input}
              type="file"
              multiple={multiple}
              accept={
                formats
                  ? formats.map((format) => `.${format}`).join(', ')
                  : undefined
              }
              onChange={handleSelectFiles}
              data-testid="upload-input"
            />
            {!disabled && (
              <Typography name="Subtitle4" color="grey400" Tag="div">
                {label}
              </Typography>
            )}
            {dragging && (
              <div ref={drag} className={styles['upload-overlay']} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
